import { OrderModel } from '@portal-clients/order';
import {
  OrderTransactionV2ListModel,
  TransactionV2ListModel,
  TransactionV2OrderModel,
} from '@portal-clients/transaction-v2';

/**
 * Applies a real-time `order` socket event to a single cached transaction,
 * mirroring the patch already applied to the transaction list cache (see
 * `use-account-transactions-query.ts`). Returns the same reference when the
 * event does not target this transaction, so callers can pass this straight
 * into `queryClient.setQueryData` without causing unnecessary re-renders.
 */
export const applyOrderEventToTransaction = (
  transaction: TransactionV2ListModel | undefined,
  order: OrderModel,
): TransactionV2ListModel | undefined => {
  if (
    !transaction ||
    transaction.type !== 'ORDER' ||
    !orderEventTargetsTransaction(transaction, order)
  ) {
    return transaction;
  }

  return {
    ...transaction,
    order: {
      id: order.verification,
      status: order.status,
      deliverAt: order.deliverAt,
      pickupAt: order.pickupAt,
      fsgmTopup: order.fsgmTopup,
      orderUrl: order.orderUrl,
    } satisfies TransactionV2OrderModel,
  };
};

/**
 * A single self-service order can be represented by more than one FSGM
 * transaction id over its lifetime — most notably a refund, which creates a
 * new financial transaction referenced by `fsgmRefundTransactionId` rather
 * than reusing the original charge's id. To keep both the original charge
 * row and any refund row live, matching falls back through:
 *
 * 1. The original charge id (`fsgmTransactionId`).
 * 2. The refund transaction id (`fsgmRefundTransactionId`), if present.
 * 3. The order's verification code, already stored as `transaction.order.id`
 *    on any previously fetched/patched row for this order.
 */
const orderEventTargetsTransaction = (
  transaction: OrderTransactionV2ListModel,
  order: OrderModel,
): boolean => {
  if (transaction.id === order.fsgmTransactionId) {
    return true;
  }

  if (
    !!order.fsgmRefundTransactionId &&
    transaction.id === order.fsgmRefundTransactionId
  ) {
    return true;
  }

  return !!transaction.order?.id && transaction.order.id === order.verification;
};
